import type { DayKey, Member } from './types'

/**
 * Who a meeting counts. A member belongs to the roster of every meeting from
 * `joinedOn` (inclusive) until `leftOn` (exclusive). Outside that window they
 * are neither present nor absent — the export shows "—" and no count includes
 * them. This is what lets someone join mid-month without being marked absent
 * for the meetings before they arrived.
 */
export function isInRoster(member: Pick<Member, 'joinedOn' | 'leftOn'>, date: DayKey): boolean {
  if (date < member.joinedOn) return false
  return member.leftOn === null || date < member.leftOn
}

export function compareMembers(a: Member, b: Member): number {
  return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'vi')
}

/** The roster of a meeting date, in display order. */
export function rosterFor(members: readonly Member[], date: DayKey): Member[] {
  return members.filter((m) => isInRoster(m, date)).sort(compareMembers)
}

/** Has stopped by `today`: a leave date on or before it. A future leave date still counts as active. */
export function hasLeftBy(member: Pick<Member, 'leftOn'>, today: DayKey): boolean {
  return member.leftOn !== null && member.leftOn <= today
}

/** Active today = in the roster of a meeting held today. */
export function isActiveOn(member: Member, today: DayKey): boolean {
  return isInRoster(member, today)
}
